import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { paymentProofService } from '@/services/paymentProofService'
import type { SubmitProofInput } from '@/types/paymentProof'

// Student hooks

export function useMyPaymentProofs() {
  return useQuery({
    queryKey: ['payment-proofs', 'me'],
    queryFn: () => paymentProofService.getMyProofs(),
  })
}

export function usePaymentProofDetail(proofId: string) {
  return useQuery({
    queryKey: ['payment-proof', proofId],
    queryFn: () => paymentProofService.getProofDetail(proofId),
    enabled: !!proofId,
  })
}

export function useSubmitPaymentProof() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      enrollmentId,
      input,
    }: {
      enrollmentId: string
      input: SubmitProofInput
    }) => paymentProofService.submitProof(enrollmentId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-proofs', 'me'] })
      queryClient.invalidateQueries({ queryKey: ['enrollments'] })
    },
  })
}

export function useCancelPaymentProof() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (proofId: string) => paymentProofService.cancelProof(proofId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-proofs', 'me'] })
    },
  })
}

// Admin hooks

export function useAdminPaymentVerifications(
  status?: string,
  page = 1,
  limit = 20
) {
  return useQuery({
    queryKey: ['admin-payment-proofs', status, page, limit],
    queryFn: () => paymentProofService.listVerifications(status, page, limit),
    staleTime: 10_000,
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  })
}

export function useAdminPaymentVerificationDetail(proofId: string) {
  return useQuery({
    queryKey: ['admin-payment-proof', proofId],
    queryFn: () => paymentProofService.getVerificationDetail(proofId),
    enabled: !!proofId,
  })
}

export function useApprovePaymentProof() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (proofId: string) => paymentProofService.approveProof(proofId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-payment-proofs'] })
      queryClient.invalidateQueries({ queryKey: ['payment-proofs', 'me'] })
      queryClient.invalidateQueries({ queryKey: ['enrollments'], exact: false })
    },
  })
}

export function useRejectPaymentProof() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proofId, reason }: { proofId: string; reason: string }) =>
      paymentProofService.rejectProof(proofId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-payment-proofs'] })
      queryClient.invalidateQueries({ queryKey: ['payment-proofs', 'me'] })
      queryClient.invalidateQueries({ queryKey: ['enrollments'], exact: false })
    },
  })
}
